import './CaseResolutionItemCard.scss';

import { Button, Card, Divider, Flex, Modal, Space, Tag, Tooltip, Typography } from 'antd';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PackageStateTag from '@/components/logistic/PackageStateTag';
import { useUserContext } from '@/contexts/UserContext';
import {
  ITEM_COMPARISON_STATUS,
  PackageItemComparison,
} from '@/model/CaseResolution/PackageItemComparison';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import {
  PACKAGE_ITEM_RESOLUTION,
  PACKAGE_ITEM_STOCK_RESOLUTION,
  PackageItemResolutionString,
  PackageItemStockResolutionString,
} from '@/model/Logistic/PackageItem/PackageItemResolution';
import { PRODUCT_STATUS } from '@/model/Logistic/PackageItem/ProductStatus';
import { ProductTypeStrings } from '@/model/Logistic/PackageItem/ProductTypes';
import { ReceivedProductStatusStrings } from '@/model/Logistic/PackageItem/ReceivedProductStatus';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { RETURN_ITEM_TYPE, ReturnItemTypeString } from '@/model/Logistic/Reception/ReturnItemType';
import {
  SHIPPING_ITEM_REFERENCE,
  ShippingItemReferenceString,
} from '@/model/Logistic/Shipment/ShipmentReference';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

import CaseResolutionItemModal from './CaseResolutionItemModal';

const { Text } = Typography;

interface CaseResolutionItemCardProps {
  roundTripComparison?: PackageItemComparison;
  nonReturnItem?: PackageItem;
  receptionForRevision?: PackageItemComparison;
  refetch: () => void;
}

const CaseResolutionItemCard = (props: CaseResolutionItemCardProps) => {
  const { t } = useTranslation();
  const { packageItemRepository } = useRepositoriesContext();
  const { userWorkArea } = useUserContext();
  const { roundTripComparison, nonReturnItem, receptionForRevision, refetch } = props;
  const [modal, contextHolder] = Modal.useModal();
  const isComparison = !!roundTripComparison;
  const isShipment = !!nonReturnItem && !receptionForRevision;
  const isReception = !!receptionForRevision && !nonReturnItem;
  const hasNotReceptionItem =
    !!roundTripComparison?.serviceOrderItem && !roundTripComparison?.receptionItem;
  // --- Datos comunes normalizados ---
  const title = isComparison
    ? `${roundTripComparison.productName}`
    : `${nonReturnItem?.name ?? receptionForRevision?.productName}`;
  const type =
    (isComparison
      ? roundTripComparison.productType
      : (nonReturnItem?.product_type ?? receptionForRevision?.productType)) ?? '';
  const status = isComparison
    ? roundTripComparison.productStatus
    : (nonReturnItem?.item_shipped_state ?? '');
  const itemStatusTag = useMemo(
    () => (
      <ItemStatusTag
        comparison={roundTripComparison}
        nonReturnItem={nonReturnItem}
        receptionForRevision={receptionForRevision}
      />
    ),
    [roundTripComparison, nonReturnItem, receptionForRevision],
  );
  const canResolve = useMemo(
    () =>
      isComparison
        ? !hasPendingPackages([roundTripComparison.shipmentItem, roundTripComparison.receptionItem])
        : !hasPendingPackages([receptionForRevision?.receptionItem]),
    [roundTripComparison, isComparison, receptionForRevision],
  );
  const cardClassName = useMemo(() => {
    const shipmentItem = isShipment
      ? nonReturnItem
      : isComparison
        ? roundTripComparison.shipmentItem
        : receptionForRevision?.shipmentItem;
    const resolution = isComparison
      ? roundTripComparison.resolution_status
      : receptionForRevision?.resolution_status;

    return isShipment &&
      shipmentItem &&
      typeof shipmentItem.package != 'number' &&
      shipmentItem.package?.status != undefined
      ? PACKAGE_STATES[shipmentItem.package.status]
      : canResolve
        ? isComparison
          ? roundTripComparison.status == ITEM_COMPARISON_STATUS.CORRECT ||
            roundTripComparison.status == ITEM_COMPARISON_STATUS.RESOLVED
            ? 'ResolvedItem'
            : roundTripComparison.status == ITEM_COMPARISON_STATUS.REVIEW_STOCK
              ? 'PendingStockItem'
              : 'ReviewItem'
          : resolution == undefined
            ? 'ReviewItem'
            : shipmentItem?.package && typeof shipmentItem.package !== 'number'
              ? shipmentItem.package.status &&
                shipmentItem.package?.status != PACKAGE_STATES.FINALIZADO
                ? PACKAGE_STATES[shipmentItem.package.status]
                : 'ResolvedItem'
              : 'ResolvedItem'
        : 'PendingPackage';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canResolve,
    roundTripComparison,
    isComparison,
    isReception,
    isShipment,
    nonReturnItem,
    receptionForRevision,
  ]);
  /**
   * Cantidades
   */
  const usedAmount = roundTripComparison?.receptionItem
    ? (roundTripComparison.usedAmount ?? 0)
    : (roundTripComparison?.serviceOrderItem?.used_amount ?? 0);
  // Motivo de retorno
  const returnReason = isComparison
    ? roundTripComparison.returnItemType != undefined
      ? roundTripComparison.shipmentReference === SHIPPING_ITEM_REFERENCE.PREVENTIVO &&
        usedAmount > 0
        ? t(ReturnItemTypeString[RETURN_ITEM_TYPE.DEVOLUCION_UTILIZADO])
        : t(ReturnItemTypeString[roundTripComparison.returnItemType])
      : roundTripComparison.receptionItem?.is_technician_item
        ? roundTripComparison.receptionItem.comment
        : '—'
    : '';

  // --- Contenido dinámico ---
  return (
    <Card
      className={`CaseResolutionItemCard ${cardClassName}`}
      classNames={{
        header: 'CaseResolutionItemCardHeader',
        title: 'CaseResolutionItemCardTitleContainer',
        body: 'CaseResolutionItemCardBody',
      }}
      title={
        <>
          <Space className="CaseResolutionItemCardTitle" direction="vertical" size={0}>
            <Text strong>{title}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {`${t('Tipo')}: ${t(ProductTypeStrings[type])} ${!receptionForRevision ? (status != undefined ? `| ${t('Estado')}: ${t(PRODUCT_STATUS[status])}` : '') : ''}`}
            </Text>
          </Space>
          {itemStatusTag}
        </>
      }
      style={{ marginBottom: 12 }}>
      <div className="CaseResolutionItemCardSection CaseResolutionItemCardDetails">
        {/* Comparación */}
        {isComparison && (
          <>
            <div className="CaseResolutionItemCardComparison">
              <ComparisonColumn
                title={t('Enviado')}
                items={[
                  {
                    label: t('Número de serie'),
                    value: roundTripComparison.shipmentItem?.serial_number,
                  },
                  { label: t('Cantidad enviada'), value: roundTripComparison.sentAmount },
                  {
                    label: t('Utilizado'),
                    value: usedAmount,
                  },
                ]}
              />
              <ComparisonColumn
                title={t('Recibido')}
                items={
                  hasNotReceptionItem
                    ? [
                        {
                          label: t('Estado de recepción'),
                          value: t('Sin recepción'),
                        },
                        {
                          label: t('Cantidad en stock técnico'),
                          value:
                            roundTripComparison.serviceOrderItem?.stock_technician_amount ??
                            roundTripComparison.sentAmount,
                        },
                        {
                          label: t('Cantidad descartada'),
                          value: roundTripComparison.serviceOrderItem?.discarded_amount,
                        },
                      ]
                    : [
                        {
                          label: t('Número de serie'),
                          value: roundTripComparison.receptionItem?.serial_number,
                        },
                        {
                          label: t('Cantidad recibida'),
                          value: roundTripComparison.receptionItem
                            ? roundTripComparison.receivedAmount
                            : undefined,
                        },
                        {
                          label: t('Stock del técnico'),
                          value: roundTripComparison.receptionItem
                            ? roundTripComparison.technicianStockAmount
                            : undefined,
                        },
                        {
                          label: t('Descartado'),
                          value: roundTripComparison.receptionItem
                            ? roundTripComparison.discardedAmount
                            : undefined,
                        },
                      ]
                }
              />
            </div>
            <DescriptionItem label={t('Referencia de envío')}>
              {roundTripComparison.shipmentReference != undefined
                ? t(ShippingItemReferenceString[roundTripComparison.shipmentReference])
                : '—'}
            </DescriptionItem>
            <DescriptionItem label={t('Motivo de retorno')}>{returnReason}</DescriptionItem>
            {roundTripComparison.receptionItem?.item_received_state != undefined && (
              <DescriptionItem label={t('Estado del producto recibido')}>
                {t(
                  ReceivedProductStatusStrings[
                    roundTripComparison.receptionItem.item_received_state
                  ],
                )}
              </DescriptionItem>
            )}
          </>
        )}

        {/* Solo envío */}
        {isShipment && (
          <>
            {nonReturnItem.serial_number && (
              <DescriptionItem label={t('Número de serie')}>
                {nonReturnItem?.serial_number}
              </DescriptionItem>
            )}
            <DescriptionItem label={t('Cantidad enviada')}>
              {nonReturnItem?.execute_amount}
            </DescriptionItem>
            <DescriptionItem label={t('Referencia')}>
              {t(ShippingItemReferenceString[nonReturnItem?.item_shipped_reference]) || '—'}
            </DescriptionItem>
          </>
        )}

        {/* Solo recepción */}
        {isReception && (
          <>
            <ComparisonColumn
              title={t('Recibido')}
              items={[
                {
                  label: t('Número de serie'),
                  value: receptionForRevision.receptionItem?.serial_number,
                },
                {
                  label: t('Cantidad recibida'),
                  value: receptionForRevision.receptionItem?.execute_amount,
                },
              ]}
            />
            <ComparisonColumn
              title={t('Enviado')}
              items={[
                {
                  label: t('Cantidad enviada'),
                  value: receptionForRevision.shipmentItem ? receptionForRevision.sentAmount : 0,
                },
              ]}
            />

            <DescriptionItem label={t('Motivo de recepción')}>
              {receptionForRevision?.receptionItem?.return_type != undefined
                ? ReturnItemTypeString[receptionForRevision.receptionItem.return_type]
                : '—'}
            </DescriptionItem>
          </>
        )}
      </div>

      {!isShipment && (
        <>
          <Divider style={{ margin: '0' }} />
          <Flex
            className="CaseResolutionItemCardSection"
            gap={16}
            justify="space-between"
            align="center">
            <Space direction="vertical" size={4}>
              {roundTripComparison?.resolution_status != undefined ||
              receptionForRevision?.resolution_status != undefined ? (
                <>
                  <Text strong>{t('Acción realizada')}:</Text>
                  <Text>
                    {isComparison
                      ? t(PackageItemResolutionString[roundTripComparison.resolution_status!])
                      : t(PackageItemResolutionString[receptionForRevision!.resolution_status!])}
                  </Text>
                </>
              ) : (
                <>
                  <Text strong>{t('Acción sugerida')}:</Text>
                  <Text>
                    {isComparison
                      ? getSuggestedAction(roundTripComparison)
                      : isReception && t('Verificar solicitud de reparación o revisión')}
                  </Text>
                </>
              )}
            </Space>
            {userWorkArea == USER_WORK_AREA.LOGISTICS &&
              canResolve &&
              ((isComparison &&
                roundTripComparison?.receptionItem &&
                roundTripComparison?.resolution_status !=
                  PACKAGE_ITEM_RESOLUTION.RESOLUCION_AUTOMATICA) ||
                (isReception && receptionForRevision)) && (
                <CaseResolutionItemModal
                  receptionItem={
                    isComparison
                      ? roundTripComparison.receptionItem!
                      : receptionForRevision!.receptionItem!
                  }
                  refetch={refetch}
                />
              )}
          </Flex>
        </>
      )}
      {isComparison && (
        <>
          <Divider style={{ margin: '0' }} />
          <Flex
            className="CaseResolutionItemCardSection"
            gap={16}
            justify="space-between"
            align="center">
            <Space direction="vertical" size={4}>
              <Text strong>{t('Estado del stock')}:</Text>
              <Text>
                {t(
                  PackageItemStockResolutionString[
                    roundTripComparison.stock_resolution_status ?? 0
                  ],
                )}
              </Text>
            </Space>
            {userWorkArea == USER_WORK_AREA.LOGISTICS &&
              canResolve &&
              roundTripComparison.receptionItem &&
              roundTripComparison.stock_resolution_status !=
                PACKAGE_ITEM_STOCK_RESOLUTION.RESUELTO &&
              roundTripComparison.status !== ITEM_COMPARISON_STATUS.CORRECT && (
                <>
                  <Button
                    onClick={() =>
                      void modal.confirm({
                        title: t('¿Desea resolver stock del ítem?'),
                        content: t('Esta acción no se puede deshacer'),
                        okText: t('Aceptar'),
                        cancelText: t('Cancelar'),
                        onOk: async () => {
                          const receptionItem = roundTripComparison.receptionItem;

                          if (!receptionItem?.id) {
                            return;
                          }

                          // La resolución manual de stock aplica solo para items con recepción.
                          void packageItemRepository
                            .updatePackageItem(receptionItem.id, {
                              stock_resolution_status: PACKAGE_ITEM_STOCK_RESOLUTION.RESUELTO,
                            })
                            .then(() => {
                              void refetch();
                            });
                        },
                      })
                    }>
                    {t('Resolver stock')}
                  </Button>
                  {contextHolder}
                </>
              )}
          </Flex>
        </>
      )}
    </Card>
  );
};

interface DescriptionItemProps {
  label: string;
  children: ReactNode;
}

const DescriptionItem = (props: DescriptionItemProps) => {
  return (
    <Flex gap={4} className="DescriptionItem">
      <Text type="secondary">{props.label}:</Text> {props.children}
    </Flex>
  );
};

interface ComparisonColumnProps {
  title: string;
  items: { label: string; value?: number | string }[];
}

const ComparisonColumn = (props: ComparisonColumnProps) => {
  const { title, items } = props;

  return (
    <div className="CaseResolutionItemCardComparisonColumn">
      <Text className="CaseResolutionItemCardComparisonTitle" strong>
        {title}
      </Text>
      <Space className="CaseResolutionItemCardComparisonValues" direction="vertical" size={4}>
        {items.map(item =>
          item.value != undefined ? (
            <div key={`${title}-${item.label}`} className="CaseResolutionItemCardComparisonValue">
              <DescriptionItem label={item.label}>{item.value ?? '—'}</DescriptionItem>
            </div>
          ) : null,
        )}
      </Space>
    </div>
  );
};

export default CaseResolutionItemCard;

// ---------- Helpers ----------
interface ItemStatusTagProps {
  comparison?: PackageItemComparison;
  nonReturnItem?: PackageItem;
  receptionForRevision?: PackageItemComparison;
}

const ItemStatusTag = (props: ItemStatusTagProps) => {
  const { t } = useTranslation();
  const { comparison, nonReturnItem, receptionForRevision } = props;

  if (nonReturnItem) {
    const packageStatus = getItemPackageStatus(nonReturnItem);

    if (packageStatus !== undefined) {
      return <PackageStateTag packageStatus={packageStatus} />;
    }

    return null;
  }

  if (comparison) {
    if (hasPendingPackages([comparison.shipmentItem, comparison.receptionItem])) {
      return (
        <Tooltip title={t('Finalice los paquetes asociados para determinar el estado final')}>
          <Tag style={{ height: 'fit-content' }} color="blue">
            {t('Esperando Finalización')}
          </Tag>
        </Tooltip>
      );
    }

    const { color, label } = getComparisonTagData(comparison.status);

    return (
      <Tag style={{ height: 'fit-content' }} color={color}>
        {t(label)}
      </Tag>
    );
  }

  if (receptionForRevision) {
    if (hasPendingPackages([receptionForRevision.receptionItem])) {
      return (
        <Tooltip title={t('Finalice la recepción asociada para determinar el estado final')}>
          <Tag style={{ height: 'fit-content' }} color="blue">
            {t('Esperando Finalización')}
          </Tag>
        </Tooltip>
      );
    }

    if (receptionForRevision.resolution_status == undefined) {
      return (
        <Tag style={{ height: 'fit-content' }} color="gold">
          {t('Revisar')}
        </Tag>
      );
    } else {
      if (
        receptionForRevision.shipmentItem?.package &&
        typeof receptionForRevision.shipmentItem?.package != 'number'
      ) {
        const status = receptionForRevision.shipmentItem.package.status;

        if (status && status != PACKAGE_STATES.FINALIZADO) {
          return <PackageStateTag packageStatus={status} />;
        }
      } else {
        return (
          <Tag style={{ height: 'fit-content' }} color="green">
            {t('Resuelto')}
          </Tag>
        );
      }
    }
  }

  return null;
};

const getComparisonTagData = (status: ITEM_COMPARISON_STATUS): { color: string; label: string } => {
  switch (status) {
    case ITEM_COMPARISON_STATUS.CORRECT:
      return { color: 'green', label: 'Resolución automática' };
    case ITEM_COMPARISON_STATUS.REVIEW:
      return { color: 'orange', label: 'Revisar' };
    case ITEM_COMPARISON_STATUS.REVIEW_STOCK:
      return { color: 'volcano', label: 'Pendiente de resolución de stock' };
    case ITEM_COMPARISON_STATUS.RESOLVED:
      return { color: 'green', label: 'Resuelto' };
    default:
      return { color: 'orange', label: 'Revisar' };
  }
};

const hasPendingPackages = (items: (PackageItem | undefined)[]) =>
  items.some(item => {
    const status = getItemPackageStatus(item);

    return (
      status !== undefined &&
      status !== PACKAGE_STATES.FINALIZADO &&
      status !== PACKAGE_STATES.CANCELADO
    );
  });
const getItemPackageStatus = (item?: PackageItem): PACKAGE_STATES | undefined => {
  if (!item) {
    return undefined;
  }

  const { package: pkg } = item;

  if (!pkg || typeof pkg === 'number') {
    return undefined;
  }

  return pkg.status;
};

const getSuggestedAction = (comparison: PackageItemComparison): string => {
  switch (comparison.status) {
    case ITEM_COMPARISON_STATUS.CORRECT:
      return 'No requiere acción';
    case ITEM_COMPARISON_STATUS.REVIEW:
      return 'Verificar uso, estado o resultado del peritaje';
    default:
      return '—';
  }
};
